import type { RosettaSDK } from "@product/rosetta-sdk";
import { useAsync } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { rosettaAtom } from "~/lib/rosetta";

import type { BillingCycle, Plan, Resource, Variant } from "./types";

export const usePricing = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const [{ result }, { execute }] = useAsync(async (sdk: RosettaSDK) => {
    const facts = await sdk.loadPostiesFacts();
    return facts?.pricing;
  }, asyncRosettaState?.result?.instance?.rulesEngine?.getFact("posties.pricing"));

  useEffect(() => {
    const sdk = asyncRosettaState?.result?.instance;
    if (!sdk) return;
    void execute(sdk);
  }, [asyncRosettaState?.result?.instance, execute]);

  return {
    pricing: result,
  };
};

export const useTracking = (variant: Variant) => {
  const triggerPoint = variant === "brochure" ? "Posties brochure" : "Posties paywall";

  const trackBillingCycleToggle = useCallback(
    (cycle: BillingCycle) => {
      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plan term toggle/Click` as const,
        category: "Subscribe to SCMP",
        label: cycle === "1y" ? "Yearly" : "Monthly",
      });
    },
    [triggerPoint],
  );

  const trackPlanSelected = useCallback(
    (plan: Plan) => {
      const { billingCycle, resource } = plan;
      const pianoTermNames: Record<`${Resource}|${BillingCycle}`, string> = {
        "postiesEPaperAndDigital|1m": "Monthly: Posties ePaper + Digital",
        "postiesEPaperAndDigital|1y": "Annual: Posties ePaper + Digital",
        "postiesPrintAndDigital|1m": "Monthly: Posties Print + Digital",
        "postiesPrintAndDigital|1y": "Annual: Posties Print + Digital",
      };
      const label = pianoTermNames[`${resource}|${billingCycle}`];

      sendGATracking({
        action: `Subscribe to SCMP/${triggerPoint}/Plans/Click` as const,
        category: "Subscribe to SCMP",
        label,
      });
    },
    [triggerPoint],
  );

  const trackCompareBenefitClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Compare benefits/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackSubscribeClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Subscribe/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  const trackRedeemClick = useCallback(() => {
    sendGATracking({
      action: `Subscribe to SCMP/${triggerPoint}/Redeem/Click` as const,
      category: "Subscribe to SCMP",
      label: "",
    });
  }, [triggerPoint]);

  return {
    trackBillingCycleToggle,
    trackCompareBenefitClick,
    trackPlanSelected,
    trackRedeemClick,
    trackSubscribeClick,
  };
};
